import React, { useRef, useState } from "react";
import { UserDetails, useUser } from "../../components/UserContext";
import { getUser, putProfile } from "../../../../shared/services/services";
import { notifyError, notifySuccess, notifyWarning } from "../../../../assets/toastService";
import { useImageUpload } from "src/shared/modules/profile/hooks/useImageUpload";
import Image from "src/shared/Image";

type Props = {
  user: UserDetails;
  prodileId?: any;
};

function WorkProfile({ user, prodileId }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [formData, setFormData] = useState({
    organization_name: user.organization_name || "",
    user_name: user.user_name || "",
    name: user.name || "",
    organization_arabic_name: user.organization_arabic_name || "",
    business_type: user.business_type || "",
    email_id: user.email_id || "",
    address_line_1: user.address_line_1 || "",
    state: user.state || "",
    country: user.country || "",
    postal_code: user.postal_code || "",
    date_of_birth: user.date_of_birth || "",
    phone_number: user.phone_number || "",
    business_location: user.business_location || "",
  });

  const { uploadImage } = useImageUpload();
  const { setUser } = useUser();

  // ✅ Phone validation
  const isPhoneValid = formData.phone_number.length === 8;

  const handleChange = (key: keyof typeof formData, value: string) => {
    if (key === "phone_number") {
      const numericValue = value.replace(/\D/g, "").slice(0, 8);
      setFormData((prev) => ({
        ...prev,
        phone_number: numericValue,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpdate = () => {
    if (!isPhoneValid) {
      notifyWarning("Phone number must be exactly 8 digits");
      return;
    }

    setIsEditing(false);

    putProfile(formData, prodileId)
      .then(() => {
        notifySuccess("Profile updated successfully");

        getUser()
          .then((res) => setUser(res.data.user_details))
          .catch((err) => console.error(err));
      })
      .catch((error) => {
        console.error(error);
        notifyError(error?.message || "Update failed");
      });
  };

  const renderField = (label: string, field: keyof typeof formData, readonly: boolean = false) => (
    <div className="w-full flex flex-col md:flex-row gap-2 text-base md:text-lg">
      <p className="md:w-1/3 font-medium">{label}</p>

      <div className="md:w-2/3">
        {isEditing && !readonly ? (
          <>
            <input
              type="text"
              value={formData[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              maxLength={field === "phone_number" ? 8 : undefined}
              inputMode={field === "phone_number" ? "numeric" : undefined}
              className={`w-full border px-3 py-2 rounded ${formData[field] !== (user[field] || "") ? "border-blue-500" : "border-gray-300"}`}
            />

            {/* ✅ Helper text */}
            {field === "phone_number" && formData.phone_number.length > 0 && formData.phone_number.length < 8 && <p className="text-red-500 text-sm mt-1">Phone number must be exactly 8 digits</p>}
          </>
        ) : (
          <p className="break-words">: {formData[field]}</p>
        )}
      </div>
    </div>
  );

  const isFormUnchanged = () => Object.keys(formData).every((key) => formData[key as keyof typeof formData] === (user[key as keyof typeof user] || ""));

  const handleImageBoxClick = () => fileInputRef.current?.click();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const img = document.createElement("img");

    reader.onload = () => {
      if (typeof reader.result === "string") img.src = reader.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxWidth = 200;
      const scale = maxWidth / img.width;

      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const compressed = new File([blob], "logo.png", {
            type: "image/png",
          });

          uploadImage(compressed, `/pos/image/${prodileId}`, "put", "business_logo.png", `Logo${user?.organization_name}`);
        } else {
          notifyWarning("Image PNG format required");
        }
      }, "image/png");
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="w-full h-full overflow-auto p-4 flex justify-center">
      <div className="w-full max-w-2xl border border-gray-200 shadow-xl rounded-2xl p-6 bg-white h-fit">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-4 border-b pb-4">
          <div onClick={handleImageBoxClick} className="w-28 h-28 rounded-full overflow-hidden border cursor-pointer">
            <Image imagePath={user?.image_url} className="w-full h-full object-cover" />
            <input type="file" ref={fileInputRef} onChange={handleImageChange} className="hidden" />
          </div>

          <div className="flex flex-col items-center md:items-start flex-1">
            <h1 className="text-2xl font-semibold mb-2">Business Profile</h1>

            <div className="flex flex-wrap gap-2">
              <button onClick={() => setIsEditing(!isEditing)} className="bg-blue-600 px-4 py-2 text-white rounded-full">
                Edit Profile
              </button>

              <button className="bg-green-500 px-4 py-2 text-white rounded-full">Membership Plan</button>
            </div>
          </div>
        </div>

        {/* Fields */}
        <div className="mt-6 space-y-4">
          <h2 className="text-xl font-semibold">Business Profile Details</h2>

          {renderField("User Name", "user_name", true)}
          {renderField("Name", "name")}
          {renderField("Organization Name", "organization_name", true)}
          {renderField("Organization 2nd Name", "organization_arabic_name")}
          {renderField("Business Type", "business_type", true)}
          {renderField("Date of Birth", "date_of_birth")}
          {renderField("Phone Number", "phone_number")}
          {renderField("Email", "email_id")}
          {renderField("Business Location", "business_location")}
          {renderField("Address Line 1", "address_line_1")}
          {renderField("State", "state")}
          {renderField("Country", "country")}
          {renderField("Postal Code", "postal_code")}
        </div>

        {/* Update */}
        {isEditing && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleUpdate}
              disabled={isFormUnchanged() || !isPhoneValid}
              className={`px-8 py-2 rounded-full text-white ${isFormUnchanged() || !isPhoneValid ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
            >
              Update
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkProfile;
